import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { useGetIdentity } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { TransitionButton } from '../actions/TransitionButton';
import { JustifiedAction } from '../actions/JustifiedAction';
import {
  SUPPORT_REASON_CODE_OPTIONS,
  SUSPENSION_PERIOD_OPTIONS,
} from '../suspensions/suspensionState';
import { suspensionEndAfter } from '../suspensions/suspensionsModel';
import { SupportCreditAction } from './SupportCreditAction';
import { TemplatePicker } from './TemplatePicker';
import {
  TICKET_SOURCE_LABEL,
  TICKET_STATUS_COLOR,
  TICKET_STATUS_LABEL,
} from './ticketsState';
import type { TicketRow as Row } from './ticketInboxModel';

/**
 * One ticket in the support inbox (D-ticket-inbox): the subject, the member, the status,
 * the source and any triggering entity, the SLA state (breached derived off the clock),
 * the assignee, and the actions the role permits. The ticket actions (assign / resolve /
 * escalate) route through the chokepoint and are audited; the member-helping actions
 * (the bounded support credit, the reused suspend) act from the ticket's member context.
 */
export const TicketRow = ({ row, now }: { row: Row; now: number }) => {
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  const { identity } = useGetIdentity();
  const ctx = { role, accessibleRegions };
  const can = (capability: Parameters<typeof hasAccess>[1]['capability']) =>
    hasAccess(ctx, { capability });

  const isResolved = row.status === 'resolved';

  return (
    <Card
      variant="outlined"
      data-testid={`ticket-row-${row.id}`}
      sx={{ mb: 2 }}
    >
      <CardContent>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Box sx={{ minWidth: 220 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {row.subject}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.memberName} | {row.market.toUpperCase()}
            </Typography>
          </Box>
          <Chip
            size="small"
            variant="outlined"
            color={TICKET_STATUS_COLOR[row.status]}
            label={TICKET_STATUS_LABEL[row.status]}
          />
          <Chip
            size="small"
            variant="outlined"
            label={TICKET_SOURCE_LABEL[row.source]}
          />
          {row.breached && (
            <Chip
              size="small"
              color="error"
              label="SLA breached"
              data-testid={`ticket-breach-${row.id}`}
            />
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid={`ticket-assignee-${row.id}`}
          >
            {row.assignee ? `Assigned to ${row.assignee}` : 'Unassigned'}
          </Typography>
        </Stack>

        {row.triggeringEntity && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ mt: 1 }}
            data-testid={`ticket-trigger-${row.id}`}
          >
            Triggered by {row.triggeringEntity.kind} #{row.triggeringEntity.id}
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {row.body}
        </Typography>

        {!isResolved && (
          <Stack
            direction="row"
            spacing={2}
            sx={{ mt: 2 }}
            useFlexGap
            flexWrap="wrap"
          >
            {can('tickets:assign') && (
              <TransitionButton
                resource="tickets"
                capability="tickets:assign"
                id={row.id}
                data={{ assignee: identity?.id ?? 'me' }}
                previousData={{ id: row.id, assignee: row.assignee }}
                label="Assign to me"
                confirmTitle="Assign this ticket"
                confirmContent="Take ownership of this ticket."
                successMessage="Ticket assigned"
              />
            )}
            {can('tickets:resolve') && (
              <TransitionButton
                resource="tickets"
                capability="tickets:resolve"
                id={row.id}
                toStatus="resolved"
                previousData={{ id: row.id, status: row.status }}
                label="Resolve"
                color="success"
                confirmTitle="Resolve this ticket"
                confirmContent="Close the ticket as resolved."
                successMessage="Ticket resolved"
              />
            )}
            {row.status !== 'escalated' && can('tickets:escalate') && (
              <TransitionButton
                resource="tickets"
                capability="tickets:escalate"
                id={row.id}
                toStatus="escalated"
                previousData={{ id: row.id, status: row.status }}
                label="Escalate"
                color="warning"
                confirmTitle="Escalate this ticket"
                confirmContent="Route this ticket to a higher hand."
                successMessage="Ticket escalated"
              />
            )}
            {can('canned_responses:read') && <TemplatePicker row={row} />}
            {can('balances:credit') && (
              <SupportCreditAction user={row.member} />
            )}
            {can('users:suspend') && (
              <JustifiedAction
                resource="users"
                capability="users:suspend"
                id={row.userId}
                label="Suspend member"
                color="warning"
                dialogTitle="Suspend this member"
                dialogIntro="A temporary hold. Pick a period and a reason code, and record why."
                reasonCodes={SUPPORT_REASON_CODE_OPTIONS}
                periods={SUSPENSION_PERIOD_OPTIONS}
                buildData={(reason, _member, code, days) => ({
                  fraudStatus: 'suspended',
                  suspensionEndsAt: suspensionEndAfter(now, days ?? 30),
                  suspensionReasonCode: code,
                  fraudReason: reason,
                })}
                previousData={{
                  id: row.userId,
                  fraudStatus: row.member.fraudStatus,
                }}
                successMessage="Member suspended"
              />
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
